import {
  Badge,
  Box,
  Button,
  Card,
  createStyles,
  Grid,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconShoppingCartPlus } from '@tabler/icons'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import ProductAlert from '@/components/commons/ProductAlert'
import BuyButton from '@/containers/showcases/BuyButton'
import {
  Product as ProductType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'

import PriceLabel from './PriceLabel'

interface Props {
  site?: SiteType
  store?: StoreType
  showcase?: ShowcaseType
  product: ProductType
  isRecommended?: boolean
  showListMode?: boolean
}

const useStyles = createStyles(theme => ({
  card: {
    boxShadow: theme.shadows.xs,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between'
  },
  listCard: { overflow: 'hidden' },
  listLayout: { display: 'flex', flex: 1 },
  listImageButton: {
    width: 160,
    flexShrink: 0,
    [theme.fn.smallerThan('sm')]: { width: '100%' }
  },
  listImageWrapper: {
    position: 'relative',
    height: '100%',
    maxHeight: 190,
    backgroundColor: theme.colors[theme.primaryColor][0]
  },
  listContent: {
    flex: 1,
    display: 'flex',
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    [theme.fn.smallerThan('sm')]: { padding: theme.spacing.sm }
  },
  listInfoButton: {
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  listModeButton: {
    '& .mantine-Button-inner': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    '& .mantine-Button-label': {
      height: 'auto'
    },
    '& .mantine-Button-icon': {
      marginBottom: theme.spacing.xs,
      marginRight: 0
    }
  },
  listSellerText: { lineHeight: 1.2 },
  listActionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 130
  },
  productImageWrapper: {
    position: 'relative',
    width: '100%',
    height: 200,
    backgroundColor: theme.colors[theme.primaryColor][0],
    [theme.fn.smallerThan('sm')]: { height: 'calc(40vw - 45px)' },
    [theme.fn.smallerThan('xs')]: { height: 'calc(60vw - 45px)' }
  },
  sellerLogo: {
    borderRadius: '50%',
    left: 10,
    height: 30,
    position: 'absolute',
    top: 10,
    width: 30,
    zIndex: 1
  }
}))

export default function ProductCard({
  site,
  store,
  showcase,
  product,
  showListMode = false,
  isRecommended = false
}: Props) {
  // Hooks
  const router = useRouter()
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // Constants
  const {
    channel: channelSlug,
    collection: collectionSlug,
    store: storeSlug,
    showcase: showcaseSlug
  } = router.query || {}
  const collectionQuery = collectionSlug ? `?collection=${collectionSlug}` : ''
  const recommendedUrl = `/${channelSlug}/${storeSlug}/${showcaseSlug}/${product.slug}${collectionQuery}`
  const productImage = product.primaryImage
    ? product.primaryImage.file
    : '/images/placeholders/store.png'
  const currentPath = router.asPath.split('?')[0]
  const productHref = isRecommended
    ? recommendedUrl
    : `${currentPath}/${product.slug}${collectionQuery}`
  const sellerDisplayName = product?.seller?.displayName

  const renderAction = ({
    iconSize,
    showListMode = false
  }: {
    iconSize: number
    showListMode?: boolean
  }) => {
    const buttonClassName = showListMode ? classes.listModeButton : ''
    const listModeButtonSx = showListMode ? { flex: 1, margin: '10px 0' } : {}
    if (!product.hasStock) {
      return (
        <Stack sx={{ flex: 1 }}>
          <ProductAlert
            productSlug={product.slug}
            className={buttonClassName}
            sx={listModeButtonSx}
          />
        </Stack>
      )
    }

    if (product.quickAddToBasket) {
      return (
        <BuyButton
          product={product}
          siteData={site}
          store={store}
          showcase={showcase}
          addToCart
          addToCartLabel="Adicionar"
          addToCartProps={{
            leftIcon: <IconShoppingCartPlus size={showListMode ? 25 : iconSize} />,
            className: buttonClassName,
            sx: { ...listModeButtonSx, paddingRight: theme.spacing.sm }
          }}
          buyNow={false}
          continueBuying={false}
        />
      )
    }

    return (
      <Stack sx={{ flex: 1 }}>
        <Button
          component={Link}
          variant="outline"
          color="green"
          fullWidth
          href={productHref}
          className={buttonClassName}
          sx={listModeButtonSx}>
          Detalhes
        </Button>
      </Stack>
    )
  }

  const renderProductTitle = ({ showListMode }: { showListMode: boolean }) => (
    <Box>
      <Title mb={2} order={isXs ? 5 : 4} sx={{ lineHeight: 1.2 }}>
        {product.title}
      </Title>
      {showListMode && !!sellerDisplayName && (
        <Text size="sm" color="dimmed" mb="xs" className={classes.listSellerText}>
          Vendido por{' '}
          <Badge
            radius="xl"
            leftSection={
              <Image
                src={product?.seller?.logo || ''}
                alt={`Vendido por ${sellerDisplayName}`}
                quality={60}
                width={20}
                height={20}
              />
            }
            sx={{ paddingLeft: 0 }}>
            {sellerDisplayName}
          </Badge>
        </Text>
      )}
      {!!product.subtitle && (
        <Text size="sm" color="dimmed" lineClamp={2} sx={{ lineHeight: 1.2 }}>
          {product.subtitle}
        </Text>
      )}
    </Box>
  )

  const renderListMode = ({ showListMode }: { showListMode: boolean }) => (
    <Box className={classes.listLayout}>
      <UnstyledButton component={Link} href={productHref} className={classes.listImageButton}>
        <Box className={classes.listImageWrapper}>
          <Image src={productImage} alt="Loja" fill style={{ objectFit: 'cover' }} quality={60} />
        </Box>
      </UnstyledButton>

      <Box className={classes.listContent}>
        <UnstyledButton component={Link} href={productHref} className={classes.listInfoButton}>
          {renderProductTitle({ showListMode })}
          <Box mt="sm">
            <PriceLabel product={product} />
          </Box>
        </UnstyledButton>
      </Box>

      <Box className={classes.listActionWrapper}>
        {renderAction({ iconSize: 20, showListMode })}
      </Box>
    </Box>
  )

  const renderGridMode = () => (
    <>
      <Card.Section>
        <UnstyledButton component={Link} href={productHref}>
          <Box className={classes.productImageWrapper}>
            {!product.isKitBundler && !!sellerDisplayName && (
              <Tooltip
                label={`Vendido por ${sellerDisplayName}`}
                openDelay={0}
                multiline
                position="right">
                <Box className={classes.sellerLogo}>
                  <Image
                    src={product?.seller?.logo || ''}
                    alt={`Vendido por ${sellerDisplayName}`}
                    quality={60}
                    width={30}
                    height={30}
                  />
                </Box>
              </Tooltip>
            )}
            <Image src={productImage} alt="Loja" fill style={{ objectFit: 'cover' }} quality={60} />
          </Box>
        </UnstyledButton>
        <Grid p="sm" justify="center" align="center" grow>
          <Grid.Col span={9} xs={12}>
            {renderProductTitle({ showListMode })}
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Card.Section>
        <Box p="sm">
          <PriceLabel product={product} />
        </Box>
        <Box p="xs">{renderAction({ iconSize: 16, showListMode })}</Box>
      </Card.Section>
    </>
  )

  return (
    <Card
      key={product.uid}
      withBorder
      p={0}
      className={`${classes.card} ${showListMode ? classes.listCard : ''}`}>
      {showListMode ? renderListMode({ showListMode }) : renderGridMode()}
    </Card>
  )
}
